/**
 * Pure helpers to build the minimal update-profile API payload (changed fields only).
 * Mirrors ArtistProfile handleSaveChanges profile branch — no React / hooks.
 */

export type ProfileFormSnapshot = {
  username: string;
  firstName: string;
  lastName: string;
  artistName: string;
  spotifyUrl: string;
  mobile: string;
  address: string;
  country: string;
  state: string;
  city: string;
  street: string;
  zipCode: string;
  shirtSize: string;
  youtubeUrl: string;
  appleUrl: string;
  instagramUrl: string;
  facebookUrl: string;
  tiktokUrl: string;
  soundcloudUrl: string;
  twitterUrl: string;
  amazonMusicUrl: string;
  deezerUrl: string;
  audiomackUrl: string;
  pandoraUrl: string;
  tidalUrl: string;
  profilePic: string;
};

export type ProfileUpdateSide = {
  profile: ProfileFormSnapshot;
  /** Distributor names (same semantics as ArtistProfile state arrays) */
  distributors: string[];
  countryCode: string;
};

/**
 * Compare/join distributors using sorted copies (equivalent to prior sort-then-join payload).
 */
function distributorsPayloadString(distributors: string[]): string {
  return [...distributors].sort().join(", ");
}

function distributorsChanged(a: string[], b: string[]): boolean {
  return JSON.stringify([...a].sort()) !== JSON.stringify([...b].sort());
}

/**
 * Build partial payload for POST update-profile: only changed fields, same keys as API.
 */
export function buildProfileUpdatePayload(
  currentData: ProfileUpdateSide,
  originalData: ProfileUpdateSide
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const cur = currentData.profile;
  const orig = originalData.profile;

  if (cur.username !== orig.username) payload.username = cur.username?.trim();
  if (cur.firstName !== orig.firstName)
    payload.firstname = cur.firstName?.trim() || "";
  if (cur.lastName !== orig.lastName) payload.lastname = cur.lastName?.trim() || "";
  if (cur.artistName !== orig.artistName)
    payload.artistname = cur.artistName?.trim() || "";
  if (cur.spotifyUrl !== orig.spotifyUrl)
    payload.spotifyUrl = cur.spotifyUrl?.trim() || undefined;

  if (distributorsChanged(currentData.distributors, originalData.distributors)) {
    payload.distributor = distributorsPayloadString(currentData.distributors);
  }

  if (
    cur.mobile !== orig.mobile ||
    currentData.countryCode !== originalData.countryCode
  ) {
    payload.mobile = `${currentData.countryCode}${cur.mobile}`;
  }

  if (cur.address !== orig.address) payload.address = cur.address?.trim() || undefined;
  if (cur.country !== orig.country) payload.country = cur.country?.trim() || undefined;
  if (cur.state !== orig.state) payload.state = cur.state?.trim() || undefined;
  if (cur.city !== orig.city) payload.city = cur.city?.trim() || undefined;
  if (cur.street !== orig.street) payload.street = cur.street?.trim() || undefined;
  if (cur.zipCode !== orig.zipCode) payload.zipCode = cur.zipCode?.trim() || undefined;
  if (cur.shirtSize !== orig.shirtSize)
    payload.shirtSize = cur.shirtSize?.trim() || undefined;
  if (cur.youtubeUrl !== orig.youtubeUrl)
    payload.youtubeUrl = cur.youtubeUrl?.trim() || undefined;
  if (cur.appleUrl !== orig.appleUrl) payload.appleUrl = cur.appleUrl?.trim() || undefined;
  if (cur.instagramUrl !== orig.instagramUrl)
    payload.instagramUrl = cur.instagramUrl?.trim() || undefined;
  if (cur.facebookUrl !== orig.facebookUrl)
    payload.facebookUrl = cur.facebookUrl?.trim() || undefined;
  if (cur.tiktokUrl !== orig.tiktokUrl)
    payload.tiktokUrl = cur.tiktokUrl?.trim() || undefined;
  if (cur.soundcloudUrl !== orig.soundcloudUrl)
    payload.soundcloudUrl = cur.soundcloudUrl?.trim() || undefined;
  if (cur.twitterUrl !== orig.twitterUrl)
    payload.twitterUrl = cur.twitterUrl?.trim() || undefined;
  if (cur.amazonMusicUrl !== orig.amazonMusicUrl)
    payload.amazonMusicUrl = cur.amazonMusicUrl?.trim() || undefined;
  if (cur.deezerUrl !== orig.deezerUrl)
    payload.deezerUrl = cur.deezerUrl?.trim() || undefined;
  if (cur.audiomackUrl !== orig.audiomackUrl)
    payload.audiomackUrl = cur.audiomackUrl?.trim() || undefined;
  if (cur.pandoraUrl !== orig.pandoraUrl)
    payload.pandoraUrl = cur.pandoraUrl?.trim() || undefined;
  if (cur.tidalUrl !== orig.tidalUrl) payload.tidalUrl = cur.tidalUrl?.trim() || undefined;
  if (cur.profilePic !== orig.profilePic)
    payload.profilePic = cur.profilePic?.trim() || undefined;

  return payload;
}
